export type ReactiveFormRequestId = string | number;

export type ReactiveFormRequestOperation = 'create' | 'update' | 'delete' | 'getById' | 'refresh' | 'submit';

export interface ReactiveFormRequestAdapterContext<
    TPayload = unknown,
    TId extends ReactiveFormRequestId = ReactiveFormRequestId,
> {
    readonly operation: ReactiveFormRequestOperation;
    readonly payload?: TPayload;
    readonly id?: TId | null;
}

export interface ReactiveFormRequestAdapter<
    TPayload = unknown,
    TResponse = unknown,
    TId extends ReactiveFormRequestId = ReactiveFormRequestId,
> {
    readonly create?: (payload: TPayload) => Promise<TResponse>;
    readonly update?: (id: TId, payload: TPayload) => Promise<TResponse>;
    readonly delete?: (id: TId, payload?: TPayload) => Promise<TResponse>;
    readonly getById?: (id: TId) => Promise<TResponse>;
    readonly refresh?: () => Promise<TResponse>;
    readonly submit?: (context: ReactiveFormRequestAdapterContext<TPayload, TId>) => Promise<TResponse>;
}

export interface ReactiveFormRequestAdapterState {
    isLoading: boolean;
    hasError: boolean;
    hasErrorCliExists: boolean;
    error: unknown | null;
}

export interface ReactiveFormRequestHandlerAdapterOptions<
    TPayload = unknown,
    TId extends ReactiveFormRequestId = ReactiveFormRequestId,
> {
    readonly operation: ReactiveFormRequestOperation;
    readonly id?: TId | null | ((payload?: TPayload) => TId | null);
}

export interface ReactiveFormRequestHandlerAdapterBridge<TResponse = unknown, TPayload = unknown> {
    readonly isLoading: boolean;
    readonly hasError: boolean;
    readonly hasErrorCliExists: boolean;
    safePerform(payload?: TPayload): void;
    response(callback: (response: TResponse) => void): void;
    unsuscribe(): void;
    perform?: (payload?: TPayload) => Promise<TResponse>;
}

export function isReactiveFormRequestId(value: unknown): value is ReactiveFormRequestId {
    return typeof value === 'string' || typeof value === 'number';
}

export function createReactiveFormRequestAdapterState(): ReactiveFormRequestAdapterState {
    return {
        isLoading: false,
        hasError: false,
        hasErrorCliExists: false,
        error: null,
    };
}

export async function executeReactiveFormRequestAdapter<
    TPayload = unknown,
    TResponse = unknown,
    TId extends ReactiveFormRequestId = ReactiveFormRequestId,
>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
    context: ReactiveFormRequestAdapterContext<TPayload, TId>,
    state: ReactiveFormRequestAdapterState = createReactiveFormRequestAdapterState(),
): Promise<TResponse> {
    state.isLoading = true;
    state.hasError = false;
    state.hasErrorCliExists = false;
    state.error = null;

    try {
        const response = await executeAdapterOperation(adapter, context);

        state.isLoading = false;
        return response;
    } catch (error) {
        state.isLoading = false;
        state.hasError = true;
        state.hasErrorCliExists = isClientExistsError(error);
        state.error = error;

        throw error;
    }
}

export function createReactiveFormRequestHandlerFromAdapter<
    TPayload = unknown,
    TResponse = unknown,
    TId extends ReactiveFormRequestId = ReactiveFormRequestId,
>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
    options: ReactiveFormRequestHandlerAdapterOptions<TPayload, TId>,
): ReactiveFormRequestHandlerAdapterBridge<TResponse, TPayload> {
    const state = createReactiveFormRequestAdapterState();
    let responseCallbacks: Array<(response: TResponse) => void> = [];

    return {
        get isLoading(): boolean {
            return state.isLoading;
        },
        get hasError(): boolean {
            return state.hasError;
        },
        get hasErrorCliExists(): boolean {
            return state.hasErrorCliExists;
        },
        safePerform: (payload?: TPayload): void => {
            void perform(payload);
        },
        response: (callback: (response: TResponse) => void): void => {
            responseCallbacks = [...responseCallbacks, callback];
        },
        unsuscribe: (): void => {
            responseCallbacks = [];
        },
        perform,
    };

    async function perform(payload?: TPayload): Promise<TResponse> {
        const response = await executeReactiveFormRequestAdapter(
            adapter,
            createReactiveFormRequestAdapterContext(
                options.operation,
                payload,
                resolveOptionId(options.id, payload),
            ),
            state,
        );

        responseCallbacks.forEach((callback) => callback(response));

        return response;
    }
}

function createReactiveFormRequestAdapterContext<
    TPayload,
    TId extends ReactiveFormRequestId,
>(
    operation: ReactiveFormRequestOperation,
    payload: TPayload | undefined,
    id: TId | null,
): ReactiveFormRequestAdapterContext<TPayload, TId> {
    const context: ReactiveFormRequestAdapterContext<TPayload, TId> = {
        operation,
        id,
    };

    if (payload === undefined) {
        return context;
    }

    return {
        ...context,
        payload,
    };
}

async function executeAdapterOperation<
    TPayload,
    TResponse,
    TId extends ReactiveFormRequestId,
>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
    context: ReactiveFormRequestAdapterContext<TPayload, TId>,
): Promise<TResponse> {
    if (adapter.submit) {
        return adapter.submit(context);
    }

    switch (context.operation) {
        case 'create':
            return executeCreate(adapter, context.payload);
        case 'update':
            return executeUpdate(adapter, context.id, context.payload);
        case 'delete':
            return executeDelete(adapter, context.id, context.payload);
        case 'getById':
            return executeGetById(adapter, context.id);
        case 'refresh':
            return executeRefresh(adapter);
        case 'submit':
            throw new Error('ReactiveFormRequestAdapter.submit is required for the submit operation.');
    }
}

function executeCreate<TPayload, TResponse, TId extends ReactiveFormRequestId>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
    payload: TPayload | undefined,
): Promise<TResponse> {
    if (!adapter.create) {
        throw new Error('ReactiveFormRequestAdapter.create is required for create operations.');
    }

    return adapter.create(payload as TPayload);
}

function executeUpdate<TPayload, TResponse, TId extends ReactiveFormRequestId>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
    id: TId | null | undefined,
    payload: TPayload | undefined,
): Promise<TResponse> {
    if (!adapter.update) {
        throw new Error('ReactiveFormRequestAdapter.update is required for update operations.');
    }

    if (!isReactiveFormRequestId(id)) {
        throw new Error('ReactiveFormRequestAdapter.update requires a record id.');
    }

    return adapter.update(id, payload as TPayload);
}

function executeDelete<TPayload, TResponse, TId extends ReactiveFormRequestId>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
    id: TId | null | undefined,
    payload: TPayload | undefined,
): Promise<TResponse> {
    if (!adapter.delete) {
        throw new Error('ReactiveFormRequestAdapter.delete is required for delete operations.');
    }

    if (!isReactiveFormRequestId(id)) {
        throw new Error('ReactiveFormRequestAdapter.delete requires a record id.');
    }

    return adapter.delete(id, payload);
}

function executeGetById<TPayload, TResponse, TId extends ReactiveFormRequestId>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
    id: TId | null | undefined,
): Promise<TResponse> {
    if (!adapter.getById) {
        throw new Error('ReactiveFormRequestAdapter.getById is required for getById operations.');
    }

    if (!isReactiveFormRequestId(id)) {
        throw new Error('ReactiveFormRequestAdapter.getById requires a record id.');
    }

    return adapter.getById(id);
}

function executeRefresh<TPayload, TResponse, TId extends ReactiveFormRequestId>(
    adapter: ReactiveFormRequestAdapter<TPayload, TResponse, TId>,
): Promise<TResponse> {
    if (!adapter.refresh) {
        throw new Error('ReactiveFormRequestAdapter.refresh is required for refresh operations.');
    }

    return adapter.refresh();
}

function resolveOptionId<TPayload, TId extends ReactiveFormRequestId>(
    id: ReactiveFormRequestHandlerAdapterOptions<TPayload, TId>['id'],
    payload?: TPayload,
): TId | null {
    if (typeof id === 'function') {
        return id(payload);
    }

    return id ?? null;
}

function isClientExistsError(error: unknown): boolean {
    if (error === null || typeof error !== 'object') {
        return false;
    }

    const errorRecord = error as Record<string, unknown>;

    return errorRecord['hasErrorCliExists'] === true || errorRecord['code'] === 'CLIENT_EXISTS' || errorRecord['status'] === 409;
}
